import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import styled from "styled-components";
import {
  Button,
  Drawer,
  Dropdown,
  Empty,
  Input,
  Menu,
  Modal,
  Spin,
  Tooltip,
  message as antdMessage,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  GlobalOutlined,
  MoreOutlined,
  PlusOutlined,
  RobotOutlined,
  SearchOutlined,
  SendOutlined,
  StopOutlined,
} from "@ant-design/icons";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import Select from "@/components/ResponsiveSelect";

import {
  deleteFacilityAiConversation,
  getFacilityAiConversationMessages,
  listFacilityAiConversations,
  streamFacilityAiChat,
  updateFacilityAiConversation,
} from "./api";
import "./styles.css";

const { TextArea } = Input;

const Composer = styled.form`
  padding: 14px clamp(16px, 4vw, 56px);
  background: #ffffff;
  border-top: 1px solid #e1e7ee;
`;

const ComposerInner = styled.div`
  box-sizing: border-box;
  padding: 12px 12px 8px;
  border: 1px solid #dbe3ec;
  border-radius: 16px;
  background: #f7f9fc;
  transition: border-color 0.2s ease;

  &:focus-within {
    border-color: #1c578f;
  }

  .ant-input {
    resize: none;
    border: 0;
    border-radius: 8px;
    background: transparent;
    box-shadow: none;
    font-size: 14px;
    padding: 4px 6px;

    &:focus,
    &:hover {
      border-color: transparent;
      box-shadow: none;
    }
  }

  @media (max-width: 767px) {
    padding: 10px 10px 6px;
  }
`;

const ComposerToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 6px;
`;

const WebSearchToggle = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  border: 1px solid ${(props) => (props.$active ? "#1c578f" : "#dbe3ec")};
  border-radius: 999px;
  background: ${(props) => (props.$active ? "#eaf4fc" : "#ffffff")};
  color: ${(props) => (props.$active ? "#1c578f" : "#68788d")};
  cursor: ${(props) => (props.$disabled ? "not-allowed" : "pointer")};
  font-size: 12px;
  font-weight: 600;
  line-height: 1.4;
  user-select: none;
  opacity: ${(props) => (props.$disabled ? 0.55 : 1)};
  transition: border-color 0.2s ease, color 0.2s ease, background 0.2s ease;
`;

const ComposerActions = styled.div`
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 8px;
`;

const TRACE_STEP_CODES = new Set(["scope", "intent", "plan", "evidence", "answer"]);
const TRACE_CHILD_CODES = new Set([
  "knowledge_base",
  "facility_database",
  "savings_calculation",
  "business_time",
  "web_search",
]);
const TRACE_STATUS = new Set(["running", "completed", "failed", "stopped"]);
const TRACE_TITLES = {
  scope: "Confirm facility scope",
  intent: "Understand request",
  plan: "Plan evidence sources",
  evidence: "Gather and verify evidence",
  answer: "Prepare answer",
};
const TRACE_CHILD_TITLES = {
  knowledge_base: "Product documentation",
  facility_database: "Facility data",
  savings_calculation: "Savings calculation",
  business_time: "Current business time",
  web_search: "Web search",
};

const emptyProgressTrace = () => ({ version: 1, steps: [], activeStepCode: null });

const normalizeProgressTrace = (payload) => {
  const rawSteps = Array.isArray(payload?.steps) ? payload.steps : [];
  const seen = new Set();
  const steps = rawSteps.reduce((result, rawStep) => {
    const code = typeof rawStep?.code === "string" ? rawStep.code : "";
    if (!TRACE_STEP_CODES.has(code) || seen.has(code)) return result;
    seen.add(code);
    const status = TRACE_STATUS.has(rawStep.status) ? rawStep.status : "running";
    const children = Array.isArray(rawStep.children)
      ? rawStep.children.reduce((childResult, rawChild) => {
          const childCode = typeof rawChild?.code === "string" ? rawChild.code : "";
          if (!TRACE_CHILD_CODES.has(childCode) || childResult.some((item) => item.code === childCode)) {
            return childResult;
          }
          const childStatus = TRACE_STATUS.has(rawChild.status) ? rawChild.status : "running";
          return [
            ...childResult,
            {
              code: childCode,
              title: TRACE_CHILD_TITLES[childCode],
              status: childStatus,
              count: Math.max(0, Math.min(Number(rawChild.count) || 0, 99)),
              summary:
                childStatus === "failed"
                  ? `${TRACE_CHILD_TITLES[childCode]} failed.`
                  : childStatus === "running"
                    ? `${TRACE_CHILD_TITLES[childCode]} is running.`
                    : `${TRACE_CHILD_TITLES[childCode]} checked.`,
            },
          ];
        }, [])
      : [];
    return [
      ...result,
      {
        code,
        title: TRACE_TITLES[code],
        status,
        summary: typeof rawStep.summary === "string" ? rawStep.summary.slice(0, 180) : "",
        children,
      },
    ];
  }, []);
  const activeStepCode = TRACE_STEP_CODES.has(payload?.active_step_code)
    ? payload.active_step_code
    : steps[steps.length - 1]?.code || null;
  return { version: 1, steps, activeStepCode };
};

const createInitialActivity = () => ({ trace: emptyProgressTrace() });

const settleActivity = (activity, status) => {
  const trace = activity?.trace || emptyProgressTrace();
  return {
    trace: {
      ...trace,
      steps: trace.steps.map((step) =>
        step.status === "running" ? { ...step, status } : step
      ),
    },
  };
};

const getCollectionItems = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.data?.results)) return payload.data.results;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const normalizeMessage = (item) => {
  const progressTrace = normalizeProgressTrace(item?.progress_trace);
  return {
    ...item,
    content_markdown: item?.content_markdown || "",
    activity: progressTrace.steps.length ? { trace: progressTrace } : item?.activity,
  };
};

const mergeAnswerContent = (streamedContent, finalContent) => {
  const streamed = typeof streamedContent === "string" ? streamedContent : "";
  const final = typeof finalContent === "string" ? finalContent : "";
  if (!streamed) return final;
  if (!final || final === streamed) return streamed;
  if (final.startsWith(streamed)) return final;
  if (streamed.startsWith(final)) return streamed;
  // A completed frame can be stale or normalized differently by the proxy.
  // Keep the already-rendered stream rather than replacing it with less data.
  return streamed;
};

const formatConversationDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
  }).format(date);
};

const ActivityTimeline = ({ item }) => {
  const trace = item?.activity?.trace;
  const steps = Array.isArray(trace?.steps) ? trace.steps : [];
  const [timelineExpanded, setTimelineExpanded] = useState(false);
  const [expandedSteps, setExpandedSteps] = useState(() => new Set());

  if (!steps.length) return null;

  const activeStep =
    steps.find((step) => step.code === trace?.activeStepCode) || steps[steps.length - 1];

  const toggleStep = (code) => {
    setExpandedSteps((current) => {
      const next = new Set(current);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  return (
    <section
      className={`facility-ai-activity ${timelineExpanded ? "is-expanded" : "is-collapsed"}`}
      aria-label="AI execution steps"
    >
      <button
        type="button"
        className="facility-ai-activity-header"
        onClick={() => setTimelineExpanded((current) => !current)}
        aria-expanded={timelineExpanded}
      >
        <span>Execution steps</span>
        <span className="facility-ai-activity-count">{steps.length} steps</span>
        <span className="facility-ai-activity-chevron" aria-hidden="true">
          {timelineExpanded ? "v" : ">"}
        </span>
      </button>
      {!timelineExpanded ? (
        <button
          type="button"
          className={`facility-ai-activity-current facility-ai-activity-item--${activeStep.status}`}
          onClick={() => setTimelineExpanded(true)}
          aria-expanded={false}
        >
          <span className="facility-ai-activity-marker" aria-hidden="true" />
          <span className="facility-ai-activity-copy">
            <strong>{activeStep.title}</strong>
            <span>{activeStep.summary}</span>
          </span>
          <span className="facility-ai-activity-chevron" aria-hidden="true">
            &gt;
          </span>
        </button>
      ) : (
        <ol className="facility-ai-activity-list">
          {steps.map((step) => {
            const hasChildren = step.children.length > 0;
            const isExpanded = expandedSteps.has(step.code);
            return (
              <li
                className={`facility-ai-activity-item facility-ai-activity-item--${step.status}`}
                key={step.code}
              >
                <span className="facility-ai-activity-marker" aria-hidden="true" />
                <div className="facility-ai-activity-content">
                  <button
                    type="button"
                    className="facility-ai-activity-toggle"
                    onClick={() => toggleStep(step.code)}
                    aria-expanded={hasChildren ? isExpanded : undefined}
                    disabled={!hasChildren}
                  >
                    <span className="facility-ai-activity-copy">
                      <strong>{step.title}</strong>
                      <span>{step.summary}</span>
                    </span>
                    {hasChildren ? (
                      <span className="facility-ai-activity-chevron" aria-hidden="true">
                        {isExpanded ? "v" : ">"}
                      </span>
                    ) : null}
                  </button>
                  {hasChildren ? (
                    <div
                      className="facility-ai-activity-children"
                      hidden={!isExpanded}
                    >
                      {step.children.map((child) => (
                        <div className="facility-ai-activity-child" key={child.code}>
                          <span className={`facility-ai-activity-child-status facility-ai-activity-child-status--${child.status}`} />
                          <span className="facility-ai-activity-child-copy">
                            <strong>{child.title}</strong>
                            <span>{child.summary}</span>
                          </span>
                          {child.count > 0 ? (
                            <span className="facility-ai-activity-child-count">x{child.count}</span>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
};

const FacilityAiAssistant = () => {
  const { companyId, facilityId } = useParams();
  const isPC = useSelector((state) => state.App?.isPC ?? true);
  const [conversations, setConversations] = useState([]);
  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [conversationsLoading, setConversationsLoading] = useState(false);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [renameConversation, setRenameConversation] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [renameSaving, setRenameSaving] = useState(false);
  const [webSearch, setWebSearch] = useState("auto");
  const [webResultsOpen, setWebResultsOpen] = useState(null);
  const [sidebarSearch, setSidebarSearch] = useState("");
  const messagesEndRef = useRef(null);
  const streamControllerRef = useRef(null);
  const scopeRef = useRef("");

  const selectedConversation = useMemo(
    () => conversations.find((conversation) => conversation.id === selectedConversationId) || null,
    [conversations, selectedConversationId]
  );

  const conversationOptions = useMemo(
    () =>
      conversations.map((conversation) => {
        const title = conversation.title || "Untitled conversation";
        const dateText = formatConversationDate(
          conversation.last_message_at || conversation.updated_at
        );
        return {
          value: conversation.id,
          label: title,
          searchText: `${title} ${dateText}`,
        };
      }),
    [conversations]
  );

  const visibleConversations = useMemo(() => {
    const keyword = sidebarSearch.trim().toLowerCase();
    if (!keyword) return conversations;
    return conversations.filter((conversation) =>
      (conversation.title || "").toLowerCase().includes(keyword)
    );
  }, [conversations, sidebarSearch]);

  const upsertConversation = useCallback((conversation) => {
    if (!conversation?.id) return;

    setConversations((current) => {
      const next = [conversation, ...current.filter((item) => item.id !== conversation.id)];
      return next.sort((left, right) => {
        const leftTime = new Date(left.last_message_at || left.updated_at || left.created_at || 0).getTime();
        const rightTime = new Date(right.last_message_at || right.updated_at || right.created_at || 0).getTime();
        return rightTime - leftTime;
      });
    });
  }, []);

  const loadConversations = useCallback(async () => {
    if (!companyId || !facilityId) return;

    setConversationsLoading(true);
    try {
      const payload = await listFacilityAiConversations(companyId, facilityId);
      setConversations(getCollectionItems(payload));
    } catch (error) {
      antdMessage.error(error?.message || "Unable to load AI conversations.");
    } finally {
      setConversationsLoading(false);
    }
  }, [companyId, facilityId]);

  const loadMessages = useCallback(
    async (conversationId) => {
      if (!conversationId || !companyId || !facilityId) return;

      setMessagesLoading(true);
      try {
        const payload = await getFacilityAiConversationMessages(companyId, facilityId, conversationId);
        setMessages(getCollectionItems(payload).map(normalizeMessage));
      } catch (error) {
        antdMessage.error(error?.message || "Unable to load this conversation.");
      } finally {
        setMessagesLoading(false);
      }
    },
    [companyId, facilityId]
  );

  useEffect(() => {
    const scopeKey = `${companyId || ""}:${facilityId || ""}`;
    scopeRef.current = scopeKey;
    streamControllerRef.current?.abort();
    setSelectedConversationId(null);
    setMessages([]);
    setWebSearch("auto");
    setWebResultsOpen(null);
    loadConversations();

    return () => {
      streamControllerRef.current?.abort();
    };
  }, [companyId, facilityId, loadConversations]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, isStreaming]);

  useEffect(() => {
    return () => {
      streamControllerRef.current?.abort();
    };
  }, []);

  const handleSelectConversation = async (conversationId) => {
    if (isStreaming || conversationId === selectedConversationId) return;

    setSelectedConversationId(conversationId);
    setMessages([]);
    await loadMessages(conversationId);
  };

  const handleNewConversation = () => {
    if (isStreaming) return;
    setSelectedConversationId(null);
    setMessages([]);
    setDraft("");
  };

  const handleRename = async () => {
    const title = renameValue.trim();
    if (!renameConversation || !title) {
      antdMessage.error("Enter a conversation name.");
      return;
    }

    setRenameSaving(true);
    try {
      const updated = await updateFacilityAiConversation(
        companyId,
        facilityId,
        renameConversation.id,
        { title }
      );
      upsertConversation(updated);
      setRenameConversation(null);
    } catch (error) {
      antdMessage.error(error?.message || "Unable to rename the conversation.");
    } finally {
      setRenameSaving(false);
    }
  };

  const handleDelete = (conversation) => {
    Modal.confirm({
      title: "Delete conversation?",
      content: "This removes the conversation and its AI context for this facility.",
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await deleteFacilityAiConversation(companyId, facilityId, conversation.id);
          setConversations((current) => current.filter((item) => item.id !== conversation.id));
          if (selectedConversationId === conversation.id) {
            setSelectedConversationId(null);
            setMessages([]);
          }
        } catch (error) {
          antdMessage.error(error?.message || "Unable to delete the conversation.");
          throw error;
        }
      },
    });
  };

  const updateStreamingMessage = useCallback((localMessageId, patch) => {
    setMessages((current) =>
      current.map((item) => (item.id === localMessageId ? { ...item, ...patch } : item))
    );
  }, []);

  const appendProgressSnapshot = useCallback((localMessageId, eventPayload) => {
    const trace = normalizeProgressTrace(eventPayload);
    setMessages((current) =>
      current.map((item) => {
        if (item.id !== localMessageId) return item;
        return {
          ...item,
          activity: { trace },
        };
      })
    );
  }, []);

  const settleStreamingActivity = useCallback((localMessageId, status) => {
    setMessages((current) =>
      current.map((item) =>
        item.id === localMessageId
          ? { ...item, activity: settleActivity(item.activity, status) }
          : item
      )
    );
  }, []);

  const appendAnswerDelta = useCallback((localMessageId, delta) => {
    if (delta === undefined || delta === null || delta === "") return;
    const nextDelta = typeof delta === "string" ? delta : String(delta);
    setMessages((current) =>
      current.map((item) =>
        item.id === localMessageId
          ? { ...item, content_markdown: `${item.content_markdown || ""}${nextDelta}` }
          : item
      )
    );
  }, []);

  const appendWebResults = useCallback((localMessageId, webResults) => {
    if (!Array.isArray(webResults) || !webResults.length) return;

    const normalized = webResults
      .filter((result) => result && typeof result.url === "string" && result.url)
      .map((result) => ({
        title: typeof result.title === "string" ? result.title : result.url,
        url: result.url,
        content: typeof result.content === "string" ? result.content : "",
      }));

    setMessages((current) =>
      current.map((item) => {
        if (item.id !== localMessageId) return item;
        const merged = [...(item.web_results || []), ...normalized];
        const seen = new Set();
        const unique = merged.filter((result) => {
          if (seen.has(result.url)) return false;
          seen.add(result.url);
          return true;
        });
        return { ...item, web_results: unique.slice(0, 10) };
      })
    );
  }, []);

  const handleSubmit = async (event) => {
    event?.preventDefault();
    const content = draft.trim();
    if (!content || isStreaming || !companyId || !facilityId) return;

    const requestId = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const localUserMessageId = `local-user-${requestId}`;
    const localAssistantMessageId = `local-assistant-${requestId}`;
    const controller = new AbortController();
    const requestScope = `${companyId}:${facilityId}`;
    let streamConversationId = selectedConversationId;

    streamControllerRef.current = controller;
    setDraft("");
    setIsStreaming(true);
    setMessages((current) => [
      ...current,
      {
        id: localUserMessageId,
        role: "user",
        content_markdown: content,
        status: "done",
        created_at: new Date().toISOString(),
      },
      {
        id: localAssistantMessageId,
        role: "assistant",
        content_markdown: "",
        status: "streaming",
        activity: createInitialActivity(),
        created_at: new Date().toISOString(),
      },
    ]);

    try {
      await streamFacilityAiChat({
        companyId,
        facilityId,
        conversationId: selectedConversationId,
        message: content,
        webSearch,
        signal: controller.signal,
        onEvent: (eventPayload) => {
          if (scopeRef.current !== requestScope) return;

          if (eventPayload.event === "conversation_created") {
            streamConversationId = eventPayload.conversation_id;
            setSelectedConversationId(eventPayload.conversation_id);
            upsertConversation(eventPayload.conversation);
            return;
          }

          if (eventPayload.event === "answer_reset") {
            updateStreamingMessage(localAssistantMessageId, {
              content_markdown: "",
              web_results: [],
            });
            return;
          }

          if (eventPayload.event === "answer_delta" || eventPayload.event === "delta") {
            appendAnswerDelta(
              localAssistantMessageId,
              eventPayload.delta ?? eventPayload.token ?? eventPayload.text ?? eventPayload.content
            );
            return;
          }

          if (eventPayload.event === "progress") {
            if (Array.isArray(eventPayload.web_results)) {
              appendWebResults(localAssistantMessageId, eventPayload.web_results);
            }
            appendProgressSnapshot(localAssistantMessageId, eventPayload);
            return;
          }

          if (eventPayload.event === "tool_result" && Array.isArray(eventPayload.web_results)) {
            appendWebResults(localAssistantMessageId, eventPayload.web_results);
          }

          if (eventPayload.event === "answer_done") {
            setMessages((current) =>
              current.map((item) => {
                if (item.id !== localAssistantMessageId) return item;
                return {
                  ...item,
                  content_markdown: mergeAnswerContent(
                    item.content_markdown,
                    typeof eventPayload.answer === "string" ? eventPayload.answer : ""
                  ),
                  status: eventPayload.error ? "error" : "done",
                  activity: settleActivity(
                    item.activity,
                    eventPayload.error ? "failed" : "completed"
                  ),
                };
              })
            );
            return;
          }

          if (eventPayload.event === "done") {
            setMessages((current) =>
              current.map((item) => {
                if (item.id !== localAssistantMessageId) return item;
                const serverMessage = eventPayload.message
                  ? normalizeMessage(eventPayload.message)
                  : {};
                const finalContent =
                  serverMessage.content_markdown || eventPayload.answer || "";
                const finalStatus = serverMessage.status || "done";
                return {
                  ...item,
                  ...serverMessage,
                  id: localAssistantMessageId,
                  content_markdown: mergeAnswerContent(item.content_markdown, finalContent),
                  status: finalStatus,
                  activity: settleActivity(
                    serverMessage.activity || item.activity,
                    finalStatus === "error" ? "failed" : "completed"
                  ),
                };
              })
            );
            return;
          }

          if (eventPayload.event === "error") {
            if (eventPayload.code === "service_error" || !eventPayload.code) {
              updateStreamingMessage(localAssistantMessageId, {
                content_markdown: eventPayload.detail || eventPayload.message || "The AI response could not be completed.",
                status: "error",
              });
            }
            settleStreamingActivity(localAssistantMessageId, "failed");
          }
        },
      });
    } catch (error) {
      const interrupted = error?.name === "AbortError";
      updateStreamingMessage(localAssistantMessageId, {
        content_markdown: interrupted
          ? "The response was stopped."
          : error?.message || "The AI response could not be completed.",
        status: "error",
      });
      settleStreamingActivity(localAssistantMessageId, interrupted ? "stopped" : "failed");
      if (!interrupted) {
        antdMessage.error(error?.message || "Unable to reach the AI service.");
      }
    } finally {
      if (streamControllerRef.current === controller) {
        streamControllerRef.current = null;
      }
      setIsStreaming(false);
      setMessages((current) =>
        current.map((item) => {
          if (item.id !== localAssistantMessageId || item.status !== "streaming") return item;
          const activity = settleActivity(item.activity, "failed");
          return {
            ...item,
            status: "error",
            content_markdown:
              item.content_markdown || "The AI response could not be completed.",
            activity,
          };
        })
      );
      if (streamConversationId && scopeRef.current === requestScope) {
        loadConversations();
      }
    }
  };

  const handleStop = () => {
    streamControllerRef.current?.abort();
  };

  const renderConversationActions = (conversation) => (
    <Menu>
      <Menu.Item
        key="rename"
        icon={<EditOutlined />}
        onClick={() => {
          setRenameConversation(conversation);
          setRenameValue(conversation.title || "");
        }}
      >
        Rename
      </Menu.Item>
      <Menu.Item key="delete" icon={<DeleteOutlined />} danger onClick={() => handleDelete(conversation)}>
        Delete
      </Menu.Item>
    </Menu>
  );

  const renderWebSourcesButton = (item) => {
    const webResults = Array.isArray(item.web_results) ? item.web_results : [];
    if (!webResults.length) return null;

    return (
      <button
        type="button"
        className="facility-ai-web-sources"
        onClick={() => setWebResultsOpen(item.id)}
        aria-label={`Show ${webResults.length} web sources`}
      >
        View web sources ({webResults.length})
      </button>
    );
  };

  return (
    <div className="facility-ai-page">
      <aside className="facility-ai-sidebar" aria-label="AI conversations">
        <div className="facility-ai-sidebar-header">
          <div>
            <div className="facility-ai-sidebar-eyebrow">Facility</div>
            <h1>AI Assistant</h1>
          </div>
          <Tooltip title="New conversation">
            <Button
              type="text"
              icon={<PlusOutlined />}
              aria-label="New conversation"
              onClick={handleNewConversation}
              disabled={isStreaming}
            />
          </Tooltip>
        </div>

        <div className="facility-ai-sidebar-search">
          <Input
            allowClear
            prefix={<SearchOutlined />}
            value={sidebarSearch}
            onChange={(event) => setSidebarSearch(event.target.value)}
            placeholder="Search conversations"
          />
        </div>

        <div className="facility-ai-conversation-list">
          {conversationsLoading ? (
            <div className="facility-ai-list-loading"><Spin size="small" /></div>
          ) : visibleConversations.length === 0 ? (
            <div className="facility-ai-list-empty">
              {sidebarSearch.trim() ? "No results found." : "No conversations yet."}
            </div>
          ) : (
            visibleConversations.map((conversation) => (
              <div
                className={`facility-ai-conversation-row${
                  conversation.id === selectedConversationId ? " is-active" : ""
                }`}
                key={conversation.id}
              >
                <button
                  type="button"
                  className="facility-ai-conversation-select"
                  onClick={() => handleSelectConversation(conversation.id)}
                  disabled={isStreaming}
                >
                  <span className="facility-ai-conversation-title">{conversation.title || "Untitled conversation"}</span>
                  <span className="facility-ai-conversation-date">
                    {formatConversationDate(conversation.last_message_at || conversation.updated_at)}
                  </span>
                </button>
                <Dropdown overlay={renderConversationActions(conversation)} trigger={["click"]} placement="bottomRight">
                  <button
                    type="button"
                    className="facility-ai-conversation-menu"
                    aria-label={`Actions for ${conversation.title || "conversation"}`}
                    disabled={isStreaming}
                  >
                    <MoreOutlined />
                  </button>
                </Dropdown>
              </div>
            ))
          )}
        </div>
      </aside>

      <main className="facility-ai-main">
        <header className="facility-ai-main-header">
          {isPC ? (
            <div className="facility-ai-heading">
              <RobotOutlined aria-hidden="true" />
              <div>
                <h2>{selectedConversation?.title || "New conversation"}</h2>
                <p>Answers are scoped to this facility.</p>
              </div>
            </div>
          ) : (
            <div className="facility-ai-h5-header">
              <Select
                showSearch
                value={selectedConversationId ?? undefined}
                placeholder="Select conversation"
                options={conversationOptions}
                loading={conversationsLoading}
                disabled={isStreaming}
                onChange={(value) => {
                  if (value) handleSelectConversation(value);
                }}
              />
              <div className="facility-ai-h5-header-actions">
                <Dropdown
                  overlay={renderConversationActions(selectedConversation)}
                  trigger={["click"]}
                  placement="bottomRight"
                >
                  <Button
                    type="text"
                    icon={<MoreOutlined />}
                    aria-label="Conversation actions"
                    disabled={!selectedConversation || isStreaming}
                  />
                </Dropdown>
                <Button
                  type="text"
                  icon={<PlusOutlined />}
                  aria-label="New conversation"
                  onClick={handleNewConversation}
                  disabled={isStreaming}
                />
              </div>
            </div>
          )}
        </header>

        <section className="facility-ai-messages" aria-live="polite">
          {messagesLoading ? (
            <div className="facility-ai-messages-loading"><Spin /></div>
          ) : messages.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Ask a question about this facility"
              className="facility-ai-empty-state"
            />
          ) : (
            messages.map((item) => (
              <article className={`facility-ai-message facility-ai-message--${item.role}`} key={item.id}>
                <div className="facility-ai-message-role">{item.role === "user" ? "You" : "AI Assistant"}</div>
                {item.role === "assistant" ? (
                  <>
                    <ActivityTimeline item={item} />
                    {item.content_markdown ? (
                      <div className="facility-ai-message-content">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{item.content_markdown}</ReactMarkdown>
                      </div>
                    ) : null}
                    {renderWebSourcesButton(item)}
                  </>
                ) : (
                  <div className="facility-ai-message-content">
                    <p>{item.content_markdown}</p>
                  </div>
                )}
              </article>
            ))
          )}
          <div ref={messagesEndRef} />
        </section>

        <Composer onSubmit={handleSubmit}>
          <ComposerInner>
            <TextArea
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              placeholder="Ask about this facility"
              autoSize={{ minRows: 1, maxRows: 6 }}
              disabled={isStreaming}
              onPressEnter={(event) => {
                if (!event.shiftKey) {
                  event.preventDefault();
                  handleSubmit(event);
                }
              }}
            />
            <ComposerToolbar>
              <WebSearchToggle
                $active={webSearch === "auto"}
                $disabled={isStreaming}
              >
                <GlobalOutlined aria-hidden="true" />
                <span>Web search</span>
              </WebSearchToggle>
              <ComposerActions>
                {isStreaming ? (
                  <Tooltip title="Stop generating">
                    <Button icon={<StopOutlined />} onClick={handleStop} aria-label="Stop generating" />
                  </Tooltip>
                ) : null}
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SendOutlined />}
                  disabled={!draft.trim() || isStreaming}
                >
                  Send
                </Button>
              </ComposerActions>
            </ComposerToolbar>
          </ComposerInner>
        </Composer>
      </main>

      <Modal
        title="Rename conversation"
        visible={Boolean(renameConversation)}
        okText="Save"
        onOk={handleRename}
        confirmLoading={renameSaving}
        onCancel={() => setRenameConversation(null)}
      >
        <Input
          autoFocus
          value={renameValue}
          maxLength={255}
          onChange={(event) => setRenameValue(event.target.value)}
          onPressEnter={handleRename}
        />
      </Modal>

      <Drawer
        title="Web sources"
        placement="right"
        width={420}
        visible={Boolean(webResultsOpen)}
        onClose={() => setWebResultsOpen(null)}
        className="facility-ai-web-drawer"
      >
        {(() => {
          const activeMessage = messages.find((item) => item.id === webResultsOpen) || null;
          const webResults = Array.isArray(activeMessage?.web_results)
            ? activeMessage.web_results
            : [];
          if (!webResults.length) {
            return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No web sources." />;
          }
          return (
            <ol className="facility-ai-web-result-list">
              {webResults.map((result) => (
                <li className="facility-ai-web-result-item" key={result.url}>
                  <a
                    className="facility-ai-web-result-title"
                    href={result.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {result.title}
                  </a>
                  {result.content ? (
                    <p className="facility-ai-web-result-content">{result.content}</p>
                  ) : null}
                </li>
              ))}
            </ol>
          );
        })()}
      </Drawer>
    </div>
  );
};

export default FacilityAiAssistant;
